// БП.1: компонент отображения ника игрока с акцентным цветом и активным титулом.
//
// Используется в местах где показывается имя игрока:
//   - ProfileScreen (свой ник)
//   - LeaderboardScreen / RatingScreen (топ игроков)
//   - ArchiveScreen / MatchDetail (оппонент)
//   - BattleScreen (оппонент)
//
// Сервер возвращает display_name (Этап 72: всегда голый ник — подпись
// Мецената стала выбираемым титулом), profile_accent_color (hex без #) и
// active_title (объект или null).
//
// Визуальный стиль цветного ника (АВ.1+): когда accentColor != null && != 'FFFFFF'
// — добавляется неон-свечение (text-shadow) в цвет ника + лёгкая пульсация
// яркости. Без перка nickname_color сервер не разрешит ставить не-белый,
// поэтому эффект автоматически только у тех у кого перк активен.

import React from 'react';
import { trTitleText } from '../i18n/serverStrings';

export interface PlayerNameProps {
  name: string;
  accentColor?: string | null;   // hex без #, например '00F0FF'
  title?: { title_code: string; title_text_ru: string } | null;
  size?: 'sm' | 'md' | 'lg';
  inline?: boolean;              // если true — титул в одной строке через '·', иначе под ником
  style?: React.CSSProperties;
}

// Сколько символов до начала свечения — для маленьких ников.
const WHITE = 'FFFFFF';

function isNeon(accentColor: string | null | undefined): accentColor is string {
  if (!accentColor) return false;
  return accentColor.toUpperCase() !== WHITE;
}

function neonStyle(accentColor: string): React.CSSProperties {
  const c = `#${accentColor}`;
  return {
    color: c,
    // Многослойный свет: близкий ореол + дальний ореол. Дальний разлетается
    // далеко, ближний делает ник яркой кромкой.
    textShadow: `
      0 0 4px ${c},
      0 0 8px ${c}aa,
      0 0 14px ${c}66
    `,
    animation: 'pn-neon-pulse 2.8s ease-in-out infinite'
  };
}

export function PlayerName({ name, accentColor, title, size = 'md', inline = false, style }: PlayerNameProps) {
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 18 : 14;
  const subSize = size === 'sm' ? 9 : size === 'lg' ? 11 : 10;

  const neon = isNeon(accentColor);
  const nameStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize,
    ...(neon ? neonStyle(accentColor!) : { color: accentColor ? `#${accentColor}` : 'var(--t-hi)' })
  };

  if (inline) {
    return (
      <span style={{ ...nameStyle, ...style }}>
        {name}
        {title && (
          <span style={{ color: 'var(--t-md)', fontWeight: 400, marginLeft: 4, textShadow: 'none' }}>
            · {trTitleText(title.title_code, title.title_text_ru)}
          </span>
        )}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2, ...style }}>
      <span style={nameStyle}>{name}</span>
      {title && (
        <span style={{ color: 'var(--t-md)', fontSize: subSize, marginTop: 1 }}>
          · {trTitleText(title.title_code, title.title_text_ru)}
        </span>
      )}
    </span>
  );
}

// Глобальный keyframe для пульсации свечения. Инжектим один раз.
if (typeof document !== 'undefined' && !document.getElementById('pn-neon-keyframes')) {
  const style = document.createElement('style');
  style.id = 'pn-neon-keyframes';
  style.textContent = `
    @keyframes pn-neon-pulse {
      0%, 100% { filter: brightness(1) saturate(1); }
      50%      { filter: brightness(1.15) saturate(1.25); }
    }
  `;
  document.head.appendChild(style);
}
